import React, { memo, useState } from 'react'
import Countdown, { zeroPad } from 'react-countdown'
import { QRCode } from 'react-qrcode-logo'
import { Redirect } from 'react-router-dom'
import moment from 'moment'
import { makeStyles } from 'transfix-ui/core'
import Box from 'transfix-ui/core/Box'
import Typography from 'transfix-ui/core/Typography'
import { brandColors } from 'transfix-ui/theme/colors'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useActivateQr } from 'apps/fleet-planner/types'
import { Spinner } from 'transfix-ui'
import FORMATS from 'constants/datetime'
import QrCodeInactiveIcon from './QrCodeInactiveIcon'
import { QrErrorDisplay } from './QrErrorDisplay'

const NINETY_SECONDS = 90_000

export const useQrDisplayStyles = makeStyles({
  qr: {
    cursor: 'pointer',
  },
  timestamp: {
    fontSize: 12,
    letterSpacing: '1.25px',
  },
  countdown: {
    color: brandColors.skyBlue6,
  },
})

interface ICountdownRendererProps {
  minutes: number
  seconds: number
}

const countdownRenderer = ({ minutes, seconds }: ICountdownRendererProps) => {
  return (
    <span>
      {minutes}:{zeroPad(seconds)}
    </span>
  )
}

interface IQrDisplayProps {
  shipmentId?: number
  driverId?: number
  motorCarrierId?: number
}

export const QrDisplay = memo(({ driverId, motorCarrierId, shipmentId }: IQrDisplayProps) => {
  const { error: activateQrError, loading: isLoadingQr, mutate: activateQr } = useActivateQr({})

  const [qrUrl, setQrUrl] = useState<string>()
  const [activationTimestamp, setActivationTimestamp] = useState<number>()

  const { trackEvent } = useFMSAnalytics()

  const generateQr = async () => {
    try {
      const { share } = await activateQr()
      const { url: qrUrl } = share || {}
      if (qrUrl) {
        trackEvent('Fraud', 'QR_CODE_GENERATION_SUCCESS', {
          shipmentId,
          motorCarrierId,
          driverId,
        })
        setActivationTimestamp(Date.now())
        setQrUrl(qrUrl)
      }
    } catch (e) {
      // Reference the above hook's error
    }
  }

  const revertToInactive = () => {
    setQrUrl(undefined)
    setActivationTimestamp(undefined)
  }

  const classes = useQrDisplayStyles()

  const timestampVerification = moment
    .tz(moment(activationTimestamp), moment.tz.guess())
    .format(FORMATS.dayMonthYearTime)

  if (activateQrError) {
    if (activateQrError.status === 401) {
      trackEvent('Fraud', 'DRIVER_VALIDATION_UNAUTHORIZED')
      return <Redirect to='/securebooking-expired' />
    }
    trackEvent('Fraud', 'QR_CODE_GENERATION_ERROR')
  }

  return (
    <Box>
      {!qrUrl && !isLoadingQr && !activateQrError && (
        <Box height='197px' className={classes.qr} onClick={generateQr}>
          <QrCodeInactiveIcon />
        </Box>
      )}
      {!qrUrl && !isLoadingQr && activateQrError && <QrErrorDisplay regenerateQr={generateQr} />}
      {!qrUrl && isLoadingQr && (
        <Box display='flex' alignItems='center' height='197px'>
          <Spinner position='relative' />
        </Box>
      )}
      {qrUrl && activationTimestamp && (
        <Box marginTop='18px'>
          <Box display='flex' flexDirection='column' alignItems='center'>
            <QRCode value={qrUrl} size={151} quietZone={5} />
            <Typography variant='caption' className={classes.timestamp}>
              {timestampVerification}
            </Typography>
          </Box>
          <Box marginTop='12px' marginBottom='16px'>
            <Typography variant='subtitle1' align='center'>
              Code expires in{' '}
              <span className={classes.countdown}>
                <Countdown
                  date={activationTimestamp + NINETY_SECONDS}
                  renderer={countdownRenderer}
                  onComplete={revertToInactive}
                />
              </span>
            </Typography>
          </Box>
        </Box>
      )}
    </Box>
  )
})
